import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import '../styles/splitFractionTileModal.scss';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import Popup from './Popup';
import ButtonComponent from './ButtonComponent';
import InputBox from './InputBox';
import { getImageSrc } from '../helper/imageBlobCache';
/**
 * Maximum number of parts allowed per tile denominator.
 * 1    → 100, 1/2 → 50, 1/3 → 34, 1/4 → 25, 1/5 → 20,
 * 1/6  → 17,  1/8 → 13, 1/10 → 10, 1/12 → 9
 */
var MAX_PARTS_MAP = {
    1: 100,
    2: 50,
    3: 34,
    4: 25,
    5: 20,
    6: 17,
    8: 13,
    10: 10,
    12: 9,
};
export var getMaxPartsForDenominator = function (denominator) {
    var _a;
    return (_a = MAX_PARTS_MAP[denominator]) !== null && _a !== void 0 ? _a : 2;
};
/**
 * Given a fraction type string like 'fraction-1-4', returns the denominator (4).
 * For 'fraction-1' returns 1.
 */
export var getDenominatorFromFractionType = function (fractionType) {
    var parts = fractionType.split('-');
    if (parts.length === 2)
        return 1;
    return parseInt(parts[parts.length - 1], 10) || 1;
};
/**
 * Given a denominator, returns the FractionType string.
 */
export var getFractionTypeFromDenominator = function (denominator) {
    if (denominator === 1)
        return 'fraction-1';
    return "fraction-1-".concat(denominator);
};
var SplitFractionTileModal = function (props) {
    var showModal = props.showModal, setShowModal = props.setShowModal, currentDenominator = props.currentDenominator, currentNumerator = props.currentNumerator, onDone = props.onDone, returnFocusTo = props.returnFocusTo;
    // ceil(fractionValue * 100) reproduces the MAX_PARTS_MAP exactly for standard
    // fractions and scales correctly for glued tiles (e.g. 13/12 → 100).
    var fractionValue = currentNumerator / (currentDenominator || 1);
    var maxParts = Math.min(Math.ceil(fractionValue * 100), 100);
    var isValidParts = function (p) { return p >= 2 && p <= maxParts; };
    var getNextValidParts = function (current, direction) {
        var next = current + (direction === 'up' ? 1 : -1);
        return (next >= 2 && next <= maxParts) ? next : null;
    };
    var getInitialParts = function () { return 2; };
    var headingRef = useRef(null);
    var _a = useState(getInitialParts()), parts = _a[0], setParts = _a[1];
    var _b = useState(''), errorMsg = _b[0], setErrorMsg = _b[1];
    var _c = useState(''), srAnnouncement = _c[0], setSrAnnouncement = _c[1];
    var handleDecrement = function () {
        var next = getNextValidParts(parts, 'down');
        if (next === null)
            return;
        setParts(next);
        setErrorMsg('');
        setSrAnnouncement(t('text.numberOfPartsValue', { value: next, defaultValue: '{{value}} parts' }));
    };
    var handleIncrement = function () {
        var next = getNextValidParts(parts, 'up');
        if (next === null)
            return;
        setParts(next);
        setErrorMsg('');
        setSrAnnouncement(t('text.numberOfPartsValue', { value: next, defaultValue: '{{value}} parts' }));
    };
    var handleInputChange = function (e) {
        var val = parseInt(e.target.value, 10);
        if (isNaN(val)) {
            setParts(0);
            setErrorMsg('');
            return;
        }
        if (val > maxParts) {
            val = maxParts;
        }
        setParts(val);
        if (val < 2) {
            setErrorMsg(t('text.minimumPartsRequired', { defaultValue: 'Minimum 2 parts required' }));
        }
        else {
            setErrorMsg('');
        }
    };
    var handleCancel = function () {
        setParts(2);
        setErrorMsg('');
        setShowModal(false);
    };
    var handleDone = function () {
        if (!isValid)
            return;
        onDone(parts, currentDenominator);
        setParts(2);
        setErrorMsg('');
        setShowModal(false);
    };
    var isValid = isValidParts(parts);
    return (_jsx(Popup, { isOpen: showModal, onClose: function () { return setShowModal(false); }, returnFocusTo: returnFocusTo, ariaLabelledBy: "splitModalTitle", initialFocusRef: headingRef, children: _jsxs("div", { className: "splitModalContainer", children: [_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }, children: srAnnouncement }), _jsx("h2", { id: "splitModalTitle", className: "splitTitle", tabIndex: -1, ref: headingRef, children: t('text.splitTileIntoParts', { defaultValue: 'Split this tile into equal-sized parts' }) }), _jsxs("div", { className: "splitInputRow", children: [_jsxs("span", { className: "splitLabel", children: [t('text.numberOfParts', { defaultValue: 'Number of parts' }), ":"] }), _jsx(ButtonComponent, { normalCSS: "splitCircleBtn ".concat(parts <= 2 ? 'disabled' : ''), disabled: parts <= 2, clickHandler: handleDecrement, ariaLabel: t('text.decreaseNumberOfParts', { defaultValue: 'Decrease number of parts' }), children: _jsx("img", { src: getImageSrc('./assets/images/icons/minusN.svg'), alt: t('text.decrease', { defaultValue: 'Decrease' }) }) }), _jsx(InputBox, { id: "splitPartsInput", normalCSS: "splitInputBox", type: "number", min: 2, max: maxParts, value: parts.toString(), inputControlledFromOutside: true, onInput: function (_id, _val, e) { return handleInputChange(e); }, invalidKeys: ['e', 'E', '+', '-', '.'], onKeyDown: function (e) {
                                if (e.key === 'Enter') {
                                    handleDone();
                                }
                            } }), _jsx(ButtonComponent, { normalCSS: "splitCircleBtn ".concat(getNextValidParts(parts, 'up') === null ? 'disabled' : ''), disabled: getNextValidParts(parts, 'up') === null, clickHandler: handleIncrement, ariaLabel: t('text.increaseNumberOfParts', { defaultValue: 'Increase number of parts' }), children: _jsx("img", { src: getImageSrc('./assets/images/icons/plusN.svg'), alt: t('text.increase', { defaultValue: 'Increase' }) }) })] }), _jsx("div", { className: "splitMaxLabel ".concat(errorMsg ? 'splitMaxLabelError' : ''), children: errorMsg
                        ? errorMsg
                        : t('text.maxPartsAllowed', { maxParts: maxParts, defaultValue: 'Maximum number allowed for this tile: {{maxParts}}' }) }), _jsxs("div", { className: "splitBtnRow", children: [_jsx(ButtonComponent, { normalCSS: "splitBtn", clickHandler: handleCancel, text: t('text.cancel', { defaultValue: 'Cancel' }) }), _jsx(ButtonComponent, { normalCSS: "splitBtn ".concat(!isValid ? 'splitBtnDisabled' : ''), clickHandler: handleDone, disabled: !isValid, text: t('text.done', { defaultValue: 'Done' }) })] }), _jsx("div", { className: "footerModal", children: _jsx("img", { src: getImageSrc('./assets/images/popupColorPatch.svg'), tabIndex: -1 }) })] }) }));
};
export default SplitFractionTileModal;
